import React, { useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { withStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Paper from '@material-ui/core/Paper'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import Avatar from '@material-ui/core/Avatar'
import CheckIcon from '@material-ui/icons/Check'
import ErrorIcon from '@material-ui/icons/Error'
import SyncIcon from '@material-ui/icons/Sync'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import green from '@material-ui/core/colors/green'
import red from '@material-ui/core/colors/red'
import theme from '../../theme'
import * as constants from '../../constants'
import gql from 'graphql-tag'

const MANUFACTURERS_QUERY = gql`
query get_manufacturers_for_sync($filter: String) {
  admin {
    manufacturers(filter: $filter) {
      manufacturerId
      displayName
      isActive
    }
  }
}
`

const styles = {
	root: {
		flexGrow: 1,
		padding: theme.spacing(3),
	},
	paper: {
		padding: theme.spacing(3),
		background: theme.palette.common.white,
		textAlign: 'center',
	},
	syncCard: {
		padding: theme.spacing(4),
		background: theme.palette.common.white,
		textAlign: 'center',
		marginBottom: theme.spacing(3),
	},
	button: {
		minWidth: 220,
		fontFamily: 'NHaasGroteskDSPro-65Md',
		margin: theme.spacing(1),
	},
	buttonProgress: {
		color: green[500],
		marginLeft: theme.spacing(1),
	},
	statusSuccess: {
		margin: '0 auto',
		backgroundColor: green[500],
		width: 48,
		height: 48,
		marginBottom: theme.spacing(1),
	},
	statusError: {
		margin: '0 auto',
		backgroundColor: red[500],
		width: 48,
		height: 48,
		marginBottom: theme.spacing(1),
	},
	resultText: {
		marginTop: theme.spacing(2),
	},
	title: {
		marginBottom: theme.spacing(3),
	},
	sectionTitle: {
		marginBottom: theme.spacing(2),
		fontWeight: 500,
	},
}

const SyncButton = ({ label, loading, onClick, classes }) => (
	<Button
		variant="contained"
		color="primary"
		className={classes.button}
		disabled={loading}
		onClick={onClick}
		startIcon={<SyncIcon />}
	>
		{label}
		{loading && <CircularProgress size={20} className={classes.buttonProgress} thickness={5} />}
	</Button>
)

const SyncResult = ({ result, classes }) => {
	if (!result) return null
	const { success, message, count } = result
	return (
		<div className={classes.resultText}>
			<Avatar className={success ? classes.statusSuccess : classes.statusError}>
				{success ? <CheckIcon /> : <ErrorIcon />}
			</Avatar>
			<Typography variant="body1" color={success ? 'textPrimary' : 'error'}>
				{success
					? `Sync completed successfully. ${count != null ? `${count} products synced.` : ''}`
					: `Sync failed: ${message || 'Unknown error'}`}
			</Typography>
		</div>
	)
}

const CECProductSync = ({ classes }) => {
	const [batteryState, setBatteryState] = useState({ loading: false, result: null })
	const [inverterState, setInverterState] = useState({ loading: false, result: null })
	const [syncAllState, setSyncAllState] = useState({ loading: false, result: null })
	const [selectedMfrId, setSelectedMfrId] = useState('')
	const [mfrSyncState, setMfrSyncState] = useState({ loading: false, result: null })

	const { data: mfrData, loading: mfrLoading } = useQuery(
		MANUFACTURERS_QUERY,
		{ variables: { filter: null }, fetchPolicy: 'cache-first' }
	)
	const manufacturers = mfrData?.admin?.manufacturers || []

	const getAuthHeaders = () => {
		const token = localStorage.getItem('id_token')
		return {
			'Accept': 'application/json',
			'Content-Type': 'application/json',
			...(token ? { 'Authorization': 'Bearer ' + token } : {}),
		}
	}

	const syncCecBatteries = async () => {
		setBatteryState({ loading: true, result: null })
		try {
			const response = await fetch(
				constants.SERVERIP + '/api/Profile/cecbattery',
				{ method: 'GET', headers: getAuthHeaders() }
			)
			const data = await response.json()
			setBatteryState({
				loading: false,
				result: {
					success: data.status === true || data.Status === true,
					count: data.count ?? data.Count,
					message: data.message ?? data.Message,
				},
			})
		} catch (err) {
			setBatteryState({
				loading: false,
				result: { success: false, message: err.message },
			})
		}
	}

	const syncCecInverters = async () => {
		setInverterState({ loading: true, result: null })
		try {
			const response = await fetch(
				constants.SERVERIP + '/api/Profile/cec_inverter',
				{ method: 'GET', headers: getAuthHeaders() }
			)
			const data = await response.json()
			setInverterState({
				loading: false,
				result: {
					success: data.status === true || data.Status === true,
					count: data.count ?? data.Count,
					message: data.message ?? data.Message,
				},
			})
		} catch (err) {
			setInverterState({
				loading: false,
				result: { success: false, message: err.message },
			})
		}
	}

	const syncAllManufacturers = async () => {
		setSyncAllState({ loading: true, result: null })
		try {
			const response = await fetch(
				constants.SERVERIP + '/api/Profile/sync_all',
				{ method: 'GET', headers: getAuthHeaders() }
			)
			const text = await response.text()
			setSyncAllState({
				loading: false,
				result: {
					success: response.ok,
					message: text,
				},
			})
		} catch (err) {
			setSyncAllState({
				loading: false,
				result: { success: false, message: err.message },
			})
		}
	}

	const syncSingleManufacturer = async () => {
		if (!selectedMfrId) return
		setMfrSyncState({ loading: true, result: null })
		try {
			const response = await fetch(
				`${constants.SERVERIP}/api/Profile/sync_manufacturer_products?manufacturerId=${selectedMfrId}`,
				{ method: 'GET', headers: getAuthHeaders() }
			)
			const text = await response.text()
			setMfrSyncState({
				loading: false,
				result: { success: response.ok, message: text },
			})
		} catch (err) {
			setMfrSyncState({
				loading: false,
				result: { success: false, message: err.message },
			})
		}
	}

	const isAnySyncing = batteryState.loading || inverterState.loading || syncAllState.loading || mfrSyncState.loading

	return (
		<div className={classes.root}>
			<Typography variant="h4" className={classes.title}>
				CEC Product Sync
			</Typography>
			<Typography variant="body2" color="textSecondary" style={{ marginBottom: theme.spacing(4) }}>
				Sync product data from the Clean Energy Council (CEC) approved listings into the staging table.
				After syncing, use &quot;Sync All Manufacturers&quot; to merge staging data into manufacturer product catalogues.
			</Typography>

			<Grid container spacing={3}>
				<Grid item xs={12} md={4}>
					<Paper className={classes.syncCard} elevation={1}>
						<Typography variant="h6" className={classes.sectionTitle}>
							CEC Batteries
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Fetch approved battery listings from CEC and import into the staging table.
						</Typography>
						<SyncButton
							label="Sync Batteries"
							loading={batteryState.loading}
							onClick={syncCecBatteries}
							classes={classes}
						/>
						<SyncResult result={batteryState.result} classes={classes} />
					</Paper>
				</Grid>

				<Grid item xs={12} md={4}>
					<Paper className={classes.syncCard} elevation={1}>
						<Typography variant="h6" className={classes.sectionTitle}>
							CEC Inverters
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Fetch approved inverter listings from CEC and import into the staging table.
						</Typography>
						<SyncButton
							label="Sync Inverters"
							loading={inverterState.loading}
							onClick={syncCecInverters}
							classes={classes}
						/>
						<SyncResult result={inverterState.result} classes={classes} />
					</Paper>
				</Grid>

				<Grid item xs={12} md={4}>
					<Paper className={classes.syncCard} elevation={1}>
						<Typography variant="h6" className={classes.sectionTitle}>
							Merge to Manufacturers
						</Typography>
						<Typography variant="body2" color="textSecondary" gutterBottom>
							Merge staged CEC products into each auto-sync manufacturer&apos;s product catalogue.
						</Typography>
						<SyncButton
							label="Sync All Manufacturers"
							loading={syncAllState.loading}
							onClick={syncAllManufacturers}
							classes={classes}
						/>
						<SyncResult result={syncAllState.result} classes={classes} />
					</Paper>
				</Grid>
			</Grid>

			<Paper className={classes.syncCard} elevation={1} style={{ marginTop: theme.spacing(3) }}>
				<Typography variant="h6" className={classes.sectionTitle}>
					Sync Single Manufacturer
				</Typography>
				<Typography variant="body2" color="textSecondary" gutterBottom>
					Select a manufacturer and sync their products from the staging table using their common names.
				</Typography>
				<Grid container spacing={2} justifyContent="center" alignItems="center" style={{ marginTop: theme.spacing(1) }}>
					<Grid item>
						<FormControl variant="outlined" size="small" style={{ minWidth: 300 }}>
							<InputLabel>Manufacturer</InputLabel>
							{mfrLoading ? (
								<Select value="" label="Manufacturer" disabled>
									<MenuItem value="">Loading...</MenuItem>
								</Select>
							) : (
								<Select
									value={selectedMfrId}
									onChange={(e) => {
										setSelectedMfrId(e.target.value)
										setMfrSyncState({ loading: false, result: null })
									}}
									label="Manufacturer"
								>
									<MenuItem value="">-- Select --</MenuItem>
									{manufacturers.map((m) => (
										<MenuItem key={m.manufacturerId} value={m.manufacturerId}>
											{m.displayName}
										</MenuItem>
									))}
								</Select>
							)}
						</FormControl>
					</Grid>
					<Grid item>
						<SyncButton
							label="Sync Selected"
							loading={mfrSyncState.loading}
							onClick={syncSingleManufacturer}
							classes={classes}
						/>
					</Grid>
				</Grid>
				<SyncResult result={mfrSyncState.result} classes={classes} />
			</Paper>

			{isAnySyncing && (
				<Typography variant="body2" color="textSecondary" style={{ marginTop: theme.spacing(2) }}>
					Sync in progress... This may take a few minutes for large datasets.
				</Typography>
			)}
		</div>
	)
}

export default withStyles(styles)(CECProductSync)
